import React, { useRef, useState } from "react";
import {
    Badge,
    Box,
    ClickAwayListener,
    Divider,
    IconButton,
    ListItemIcon,
    ListSubheader,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import BookmarkIcon from "./bookmark-icon.svg?react";
import ViewOnlyIcon from "./view-only.icon.svg?react";
import { DotLegalAlert, DotLegalButton, DotLegalChip, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useSavedViewsMenu } from "./SavedViewsMenu.hooks";
import { useSavedViewsMenuStyles } from "./SavedViewsMenu.styles";
import { listPageToolbarChangedDot } from "@common/components/listPageToolbar/ListPageToolbar.styles";
import { SavedView } from "./SavedViewsMenu.types";
import CreateSavedViewDialog from "./createSavedViewDialog/CreateSavedViewDialog";
import DeleteSavedViewDialog from "./deleteSavedViewDialog/DeleteSavedViewDialog";
import UpdateSavedViewDialog from "./updateSavedViewDialog/UpdateSavedViewDialog.tsx";
import SetDefaultSavedViewDialog from "./setDefaultSavedViewDialog/SetDefaultSavedViewDialog.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { FilterPage } from "../../pageView/PageView.types.ts";
import { isNullOrWhitespace } from "../../stringOperations.ts";
import { legalEntitiesIcon, PurpleLockIcon } from "../../icons.tsx";
import PlanDialogWrapper from "../../../plan/planDialogWrapper/planDialogWrapper.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import SetCustomerDefaultSavedViewDialog from "./setCustomerDefaultSavedViewDialog/SetCustomerDefaultSavedViewDialog.tsx";
import SetSavedViewAsGlobalDialog from "./setSavedViewAsGlobalDialog/SetSavedViewAsGlobalDialog.tsx";
import SavedViewName from "@common/components/savedViews/savedViewName/SavedViewName";
import { useIsOverflowing } from "@common/hooks/useIsOverflowing";

export interface SavedViewsMenuProps {
    parentIsLoading: boolean;
    currentFilterData: any;
    currentDisplayData: any;
    currentViewId: string | undefined;
    filterPage: FilterPage;
    scopeId?: string;
    secondaryScopeId?: string;
    hasFilterOrDisplayDataChanged: boolean;

    onViewChange: (view: SavedView) => void;
    // Optional because a picker (canManageViews false) can neither create nor delete a view
    onViewSaved?: (viewId: string) => void;
    onViewDeleted?: (viewId: string) => void;

    // A view can be locked by a page's own plan feature rather than by the saved views feature, and then the upgrade
    // text has to name that feature instead.
    lockedViewText?: string;

    // A page that only offers its .legal views as a picker has nothing to save, default or warn about, so creating,
    // managing and the changed state are left out. Defaults to true.
    canManageViews?: boolean;
}

function SavedViewsMenu(props: SavedViewsMenuProps) {
    const styles = useSavedViewsMenuStyles();

    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(null);
    const [submenuViewId, setSubmenuViewId] = useState<string | null>(null);

    const { currentFilterData, currentDisplayData, onViewChange, filterPage } = props;
    const { translateString } = useTranslation();
    const { platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();

    const {
        isLoading,
        savedViews,
        sharedViews,
        personalViews,
        currentView,
        canManageViews,
        hasChangesFromCurrentView,
        canSaveToCurrentView,
        viewToDelete,
        setViewToDelete,
        showCreateViewDialog,
        setShowCreateViewDialog,
        viewToUpdate,
        setViewToUpdate,
        showSetUserDefaultView,
        setShowSetUserDefaultView,
        menuOpen,
        setMenuOpen,
        savedViewsAnchorRef,
        menuMaxHeight,
        openMenu,
        onViewCreated,
        onViewDeleted,
        onViewUpdated,
        onToggledDefaultView,
        onSaveDataToCurrentView,
        isSavingToCurrentView,
        showSetCompanyDefaultView,
        setShowSetCompanyDefaultView,
        onToggleCustomerDefaultView,
        showSetAsGlobalDialog,
        setShowSetAsGlobalDialog,
        onViewSetToGlobal,
    } = useSavedViewsMenu(props);

    const viewName = currentView?.name ?? translateString("savedViews");
    const viewNameRef = useRef<HTMLParagraphElement>(null);
    const isViewNameCutShort = useIsOverflowing(viewNameRef, viewName);

    const hasViewPickerTooltip = isViewNameCutShort || hasChangesFromCurrentView;

    // One tooltip for both the cut off name and the changed dot, as two separate ones open on top of each other
    function getViewPickerTooltip() {
        return (
            <>
                {isViewNameCutShort && <Box sx={styles.tooltipViewName}>{viewName}</Box>}
                {hasChangesFromCurrentView && (
                    <Box sx={styles.tooltipChangedLine}>
                        <Box component="span" sx={styles.tooltipChangedDot} />
                        {translateString("viewChangedHeader")}
                    </Box>
                )}
            </>
        );
    }

    const handleDeleteDialogClose = () => {
        setViewToDelete(undefined);
    };

    const handleOpenSubmenu = (event: React.MouseEvent<HTMLElement>, view: SavedView) => {
        event.stopPropagation();
        const menuItem = event.currentTarget.closest("li");

        if (isNullOrWhitespace(submenuViewId) || submenuViewId !== view.id) {
            setSubmenuAnchorEl(menuItem);
            setSubmenuViewId(view.id);
        } else {
            setSubmenuAnchorEl(null);
            setSubmenuViewId(null);
        }
    };

    const handleCloseSubmenu = () => {
        setSubmenuAnchorEl(null);
        setSubmenuViewId(null);
    };

    // A .legal provided view may be picked as a personal default, and nothing more: it is defined in code with no
    // row behind it, so renaming, deleting and the organisation default have nothing to write to. "See all
    // contracts" is not one of them - it is the contract page's own view-only list - so it keeps no menu at all.
    const shouldShowOverflowMenuIcon = (view: SavedView) => {
        if (view.isFetchAllContractWithoutAuthView) {
            return false;
        }

        return canSetViewAsCustomerDefault(view) || canEditOrDeleteView(view) || canSetViewAsGlobal(view) || canSetViewAsUserDefault(view);
    };

    const canSetViewAsUserDefault = (view: SavedView) => {
        return !view.isUserDefault;
    };

    const canEditOrDeleteView = (view: SavedView) => {
        return (
            !view.isSystemDefault &&
            !view.isSystemProvidedView &&
            (!view.isGlobal || (view.isGlobal && permissions.contractManagementPermissions.canManageGlobalViews))
        );
    };

    const canSetViewAsGlobal = (view: SavedView) => {
        return !view.isSystemDefault && !view.isGlobal && permissions.contractManagementPermissions.canManageGlobalViews;
    };

    const canSetViewAsCustomerDefault = (view: SavedView) => {
        return (
            !view.isSystemDefault &&
            !view.isSystemProvidedView &&
            view.isGlobal &&
            !view.isCustomerDefault &&
            permissions.contractManagementPermissions.canManageGlobalViews
        );
    };

    function getMenuItem(view: SavedView) {
        const isCurrentView = view.id === props.currentViewId;
        const itemId = `view-item-${view.id}`;
        const showManageIcon = canManageViews && shouldShowOverflowMenuIcon(view) && view.hasPlanAccess;
        const isSystemOrCustomerDefaultView = view.isSystemDefault || view.isCustomerDefault;
        // "See all contracts" is system provided too but has always been shown unmarked, as the contract page's own view-only list
        const showsDotLegalIcon = isSystemOrCustomerDefaultView || (view.isSystemProvidedView && !view.isFetchAllContractWithoutAuthView);

        let menuItem = (
            <MenuItem
                key={view.id}
                onClick={() => {
                    if (view.hasPlanAccess) {
                        onViewChange(view);
                        setMenuOpen(false);
                    }
                }}
                sx={{
                    ...styles.viewRow,
                    ...(isCurrentView ? styles.selectedViewRow : {}),
                    [`&:hover #${itemId}-more`]: { visibility: "visible" },
                }}
                disabled={!view.hasPlanAccess}
            >
                <ListItemIcon sx={{ visibility: isCurrentView ? "visible" : "hidden" }}>
                    <CheckIcon sx={{ fontSize: "16px" }} />
                </ListItemIcon>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, "& > :not(:first-child)": { flexShrink: 0 } }}>
                    <SavedViewName name={view.name} description={view.description} />
                    {showsDotLegalIcon && (
                        <DotLegalTooltip
                            text={
                                view.isSystemDefault
                                    ? translateString("systemDefaultView")
                                    : view.isCustomerDefault
                                      ? translateString("customerDefaultView")
                                      : translateString("systemProvidedView")
                            }
                        >
                            <Box
                                component={"span"}
                                sx={(theme) => ({
                                    display: "flex",
                                    alignItems: "center",
                                    "& svg": {
                                        width: 16,
                                        height: 16,
                                        fill: "#DAD9EF",
                                    },
                                })}
                            >
                                {legalEntitiesIcon}
                            </Box>
                        </DotLegalTooltip>
                    )}
                    {view.isFetchAllContractWithoutAuthView && (
                        <DotLegalTooltip text={translateString("viewAllContractWithoutAuthFilterTooltip")}>
                            <Box component={"span"} sx={{ display: "flex", alignItems: "center" }}>
                                <ViewOnlyIcon style={{ width: 18, height: 18 }} />
                            </Box>
                        </DotLegalTooltip>
                    )}
                </Box>

                <Box
                    sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(0.5),
                        marginLeft: "auto",
                        paddingLeft: theme.spacing(1),
                    })}
                >
                    {canManageViews && view.isUserDefault && (
                        <DotLegalChip value={translateString("defaultChip")} size={"small"} isAutocomplete hoverText={translateString("userDefaultView")} />
                    )}
                    {!view.hasPlanAccess && <Box sx={{ display: "flex", alignItems: "center" }}>{PurpleLockIcon}</Box>}
                    {showManageIcon && (
                        <IconButton
                            id={`${itemId}-more`}
                            size="small"
                            onClick={(e) => handleOpenSubmenu(e, view)}
                            sx={{ visibility: "hidden", marginRight: -1 }}
                        >
                            <MoreVertIcon sx={{ width: 18, height: 18 }} />
                        </IconButton>
                    )}
                </Box>
            </MenuItem>
        );

        if (view.hasPlanAccess) {
            return menuItem;
        }

        const lockedViewText = props.lockedViewText ?? translateString("upgradeToPaidPlanForAccessToSavedViews");

        return (
            <PlanDialogWrapper key={view.id} enabled text={lockedViewText}>
                <DotLegalTooltip text={lockedViewText}>
                    <span>{menuItem}</span>
                </DotLegalTooltip>
            </PlanDialogWrapper>
        );
    }

    // The menu moves keyboard focus between its direct children only, so a section is a flat list of a subheader
    // and its items rather than a wrapper around them
    function getMenuSection(headerKey: string, views: Array<SavedView>) {
        if (views.length === 0) {
            return [];
        }

        // A picker only lists .legal views, which are all shared, so a heading would name the one section there is
        if (!canManageViews) {
            return views.map((view) => getMenuItem(view));
        }

        return [
            <ListSubheader key={headerKey} disableSticky sx={styles.sectionLabel}>
                {translateString(headerKey)}
            </ListSubheader>,
            ...views.map((view) => getMenuItem(view)),
        ];
    }

    function getSaveAsNewViewItem() {
        const isPlanLocked = !platformFeatures.canManagePageView;

        const menuItem = (
            <MenuItem
                sx={styles.footerRow}
                disabled={isPlanLocked || props.parentIsLoading || isSavingToCurrentView}
                onClick={() => {
                    setShowCreateViewDialog(true);
                    setMenuOpen(false);
                }}
            >
                <ListItemIcon>
                    <AddIcon sx={styles.footerAddIcon} />
                </ListItemIcon>
                <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                    {translateString("saveAsNewView")}
                </DotLegalHeader>
                {isPlanLocked && <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto", paddingLeft: 1 }}>{PurpleLockIcon}</Box>}
            </MenuItem>
        );

        if (!isPlanLocked) {
            return menuItem;
        }

        return (
            <PlanDialogWrapper enabled text={translateString("upgradeToCustomList")}>
                <DotLegalTooltip text={translateString("upgradeToCustomList")}>
                    <span>{menuItem}</span>
                </DotLegalTooltip>
            </PlanDialogWrapper>
        );
    }

    return (
        <>
            {/* Always shown, even with a single view: on most pages the menu is the only place a new view can be created */}
            {/* Switched off rather than left without text: DotLegalTooltip drops its wrapper without text, which would
                remount the button and leave the overflow check watching the old name */}
            <DotLegalTooltip text={getViewPickerTooltip()} disableHoverlistener={!hasViewPickerTooltip}>
                <Box ref={savedViewsAnchorRef} sx={styles.viewPicker}>
                    <DotLegalButton disabled={props.parentIsLoading} isLoading={isSavingToCurrentView} onClick={openMenu} buttonType={"secondary"}>
                        <Badge sx={[listPageToolbarChangedDot, styles.changedViewDot]} variant="dot" invisible={!hasChangesFromCurrentView}>
                            <BookmarkIcon width={20} height={20} />
                        </Badge>
                        <Box sx={styles.viewPickerText}>
                            <Typography ref={viewNameRef} sx={styles.viewPickerName}>
                                {viewName}
                            </Typography>
                        </Box>
                    </DotLegalButton>
                </Box>
            </DotLegalTooltip>
            <Menu
                open={menuOpen}
                anchorEl={savedViewsAnchorRef.current}
                onClose={() => setMenuOpen(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        sx: { ...styles.menuPaper, maxHeight: menuMaxHeight },
                    },
                }}
                MenuListProps={{ sx: { paddingY: canManageViews ? 0 : 0.5 } }}
            >
                {hasChangesFromCurrentView && (
                    <Box component="li" sx={{ paddingBottom: 0.5, listStyle: "none" }}>
                        <DotLegalAlert
                            severity="warning"
                            warningHeader={translateString("viewChangedHeader")}
                            warningText={translateString(canSaveToCurrentView ? "viewChangedSaveOrNewText" : "viewChangedNewViewOnlyText")}
                            sx={styles.changedViewAlert}
                        />
                    </Box>
                )}
                {isLoading ? (
                    <DotLegalFullScreenSpinner />
                ) : (
                    [...getMenuSection("sharedWithOrganisation", sharedViews), ...getMenuSection("onlyMe", personalViews)]
                )}
                {canManageViews && <Divider component="li" sx={styles.footerDivider} />}
                {canSaveToCurrentView && (
                    <MenuItem
                        sx={styles.footerRow}
                        disabled={props.parentIsLoading}
                        onClick={() => {
                            onSaveDataToCurrentView(props.currentViewId!);
                            handleCloseSubmenu();
                            setMenuOpen(false);
                        }}
                    >
                        <ListItemIcon>
                            <CheckIcon sx={{ fontSize: "16px" }} />
                        </ListItemIcon>
                        <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                            {translateString("saveChangesToThisView")}
                        </DotLegalHeader>
                    </MenuItem>
                )}
                {canManageViews && getSaveAsNewViewItem()}
            </Menu>
            <Popper
                open={submenuAnchorEl !== null}
                anchorEl={submenuAnchorEl}
                placement="right-start"
                modifiers={[
                    { name: "flip", enabled: true, options: { fallbackPlacements: ["left-start", "right-end"] } },
                    { name: "preventOverflow", enabled: true, options: { boundary: "viewport", padding: 8 } },
                ]}
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={handleCloseSubmenu}>
                    <Paper sx={{ ...styles.menuPaper, minWidth: 180, marginLeft: 0.5 }}>
                        <MenuList>
                            {(() => {
                                const submenuView = savedViews?.find((x) => x.id === submenuViewId);
                                if (!submenuView) return null;

                                return (
                                    <>
                                        {canSetViewAsUserDefault(submenuView) && (
                                            <MenuItem
                                                onClick={() => {
                                                    setShowSetUserDefaultView(submenuView);
                                                    handleCloseSubmenu();
                                                    setMenuOpen(false);
                                                }}
                                            >
                                                <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                                                    {translateString("setAsDefault")}
                                                </DotLegalHeader>
                                            </MenuItem>
                                        )}

                                        {canSetViewAsCustomerDefault(submenuView) && (
                                            <MenuItem
                                                onClick={() => {
                                                    setShowSetCompanyDefaultView(submenuView);
                                                    handleCloseSubmenu();
                                                    setMenuOpen(false);
                                                }}
                                            >
                                                <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                                                    {translateString("setAsOrganisationDefaultView")}
                                                </DotLegalHeader>
                                            </MenuItem>
                                        )}

                                        {canSetViewAsGlobal(submenuView) && (
                                            <MenuItem
                                                onClick={() => {
                                                    setShowSetAsGlobalDialog(submenuView);
                                                    handleCloseSubmenu();
                                                    setMenuOpen(false);
                                                }}
                                            >
                                                <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                                                    {translateString("saveAsGlobalView")}
                                                </DotLegalHeader>
                                            </MenuItem>
                                        )}

                                        {canEditOrDeleteView(submenuView) && (
                                            <>
                                                <MenuItem
                                                    onClick={() => {
                                                        setViewToUpdate(submenuView.id);
                                                        handleCloseSubmenu();
                                                        setMenuOpen(false);
                                                    }}
                                                >
                                                    <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                                                        {translateString("editViewName")}
                                                    </DotLegalHeader>
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={() => {
                                                        setViewToDelete(submenuView);
                                                        handleCloseSubmenu();
                                                        setMenuOpen(false);
                                                    }}
                                                >
                                                    <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle={"small"}>
                                                        {translateString("delete")}
                                                    </DotLegalHeader>
                                                </MenuItem>
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
            {showCreateViewDialog && (
                <CreateSavedViewDialog
                    onClose={() => setShowCreateViewDialog(false)}
                    onViewSaved={onViewCreated}
                    displayConfiguration={currentDisplayData}
                    filterConfiguration={currentFilterData}
                    savedViewType={filterPage}
                    scopeId={props.scopeId}
                    secondaryScopeId={props.secondaryScopeId}
                />
            )}
            {!isNullOrWhitespace(viewToUpdate) && savedViews && savedViews.length > 0 && (
                <UpdateSavedViewDialog
                    onClose={() => setViewToUpdate(undefined)}
                    onViewUpdated={onViewUpdated}
                    displayConfiguration={currentDisplayData}
                    filterConfiguration={currentFilterData}
                    view={savedViews.find((x) => x.id == viewToUpdate)!}
                />
            )}
            {showSetUserDefaultView && (
                <SetDefaultSavedViewDialog
                    view={showSetUserDefaultView}
                    savedViewType={props.filterPage}
                    scopeId={props.scopeId}
                    secondaryScopeId={props.secondaryScopeId}
                    onClose={() => setShowSetUserDefaultView(undefined)}
                    onSuccess={onToggledDefaultView}
                />
            )}
            {showSetCompanyDefaultView && (
                <SetCustomerDefaultSavedViewDialog
                    view={showSetCompanyDefaultView}
                    savedViewType={props.filterPage}
                    scopeId={props.scopeId}
                    secondaryScopeId={props.secondaryScopeId}
                    onClose={() => setShowSetCompanyDefaultView(undefined)}
                    onSuccess={onToggleCustomerDefaultView}
                />
            )}

            {showSetAsGlobalDialog && (
                <SetSavedViewAsGlobalDialog
                    view={showSetAsGlobalDialog}
                    savedViewType={props.filterPage}
                    onClose={() => setShowSetAsGlobalDialog(undefined)}
                    onSuccess={onViewSetToGlobal}
                />
            )}
            {viewToDelete && <DeleteSavedViewDialog view={viewToDelete} onCloseDialog={handleDeleteDialogClose} onSuccessDelete={onViewDeleted} />}
        </>
    );
}

export default SavedViewsMenu;
